import React from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage, defineMessages } from 'react-intl';
import { connect } from 'react-redux';
import { Link } from 'react-router';
import get from 'lodash/get';

import Step from '../../components/Step';
import Container from '../../components/Container';
import Segment from '../../components/Segment';
import Dimmer from '../../components/Dimmer';
import Loader from '../../components/Loader';
import Message from '../../components/Message';
import Header from '../../components/Header';
import WCAGTab from '../../components/WCAGTab';

import cognitoService, { translateError } from '../../cognito/cognito-service';
import { login } from '../Authorization/actions';
import EnterEmail from './enter-email';
import VerifySetPassword from './verify-set-password';
import Success from './success';
import PageTitle from '../../components/PageTitle';

const messages = defineMessages({
  NotAuthorizedException: {
    id: 'forgot-password.notAuthorized',
    defaultMessage: 'User not authorized to change password at this time.',
  },
  CodeMismatchException: {
    id: 'forgot-password.codeMismatch',
    defaultMessage: 'Invalid verification code provided.',
  },
  ExpiredCodeException: {
    id: 'forgot-password.invalidVerificationCode',
    defaultMessage: 'Invalid verification code provided.',
  },
  InvalidParameterException: {
    id: 'forgot-password.invalidPassword',
    defaultMessage: 'Password failed to satisfy security requirements.',
  },
  emailStep: {
    id: 'forgot-password.emailStep',
    defaultMessage: 'Enter E-mail',
  },
  passwordStep: {
    id: 'forgot-password.setPasswordStep',
    defaultMessage: 'Set Password',
  },
  stateError: {
    id: 'forgot-password.stateError',
    defaultMessage: 'Please contact your institution\'s Intersect Administrator or {supportLink} to receive a new password.',
  },
});

class ForgotPassword extends React.PureComponent {
  static defaultState = {
    cognitoUser: null,
    errorCode: null,
    requesting: false,
    stateError: false,
    step: 1,
  };

  static propTypes = {
    current: PropTypes.object,
    login: PropTypes.func.isRequired,
  };

  state = ForgotPassword.defaultState;

  constructor(props) {
    super(props);

    this.passwordRef = React.createRef();
    this.emailRef = React.createRef();
  }

  componentDidMount() {
    // NOTE: Focus on Email tab to make screenreader readable
    this.emailRef.current.focus();
  }

  componentWillReceiveProps({ current }) {
    const errorCode = get(current, 'viewer.errorCode');
    if (errorCode !== get(this.props, 'current.viewer.errorCode')) {
      this.setState({
        errorCode,
        requesting: false,
      });
    }
  }

  isNonRegisteredUser = (error) => (error.code === 'UserNotFoundException') &&
    (error.message === 'Username/client id combination not found.');

  onFailure = (error) => {
    if (error.code === 'NotAuthorizedException' &&
      error.message === 'User password cannot be reset in the current state.') {
      this.setState({
        requesting: false,
        stateError: true,
      });
    } else if (this.isNonRegisteredUser(error)) {
      // show same workflow for registered and non registered users.
      this.inputVerificationCode();
    } else {
      this.setState({
        requesting: false,
        errorCode: error.code,
      });
    }
  };

  inputVerificationCode = () => {
    this.setState({
      stateError: false,
      requesting: false,
      errorCode: null,
      step: 2,
    }, () => {
      this.passwordRef.current.focus();
    });
  };

  setPassword = (verificationCode, password) => {
    this.setState({
      errorCode: null,
      requesting: true,
    });
    this.state.cognitoUser.confirmPassword(verificationCode, password, {
      onSuccess: () => {
        this.setState({
          password,
          errorCode: null,
          requesting: false,
          step: 3,
        });
      },
      onFailure: this.onFailure,
    });
  };

  forgotPassword = (username) => {
    const cognitoUser = cognitoService.getCognitoUser(username);
    this.setState({
      username,
      cognitoUser,
      errorCode: null,
      requesting: true,
    });
    cognitoUser.forgotPassword({
      onFailure: this.onFailure,
      inputVerificationCode: this.inputVerificationCode,
    });
  };

  signIn = () => {
    const { username, password } = this.state;
    this.props.login({
      username,
      password,
    });
    this.setState({
      errorCode: null,
      requesting: true,
    });
  };

  render() {
    const { step, username, requesting, errorCode, stateError } = this.state;
    const errorDetails = errorCode ? (
      <Message role="alert" negative>
        <Message.Header><FormattedMessage {...translateError(errorCode)} {...messages[errorCode]} /></Message.Header>
      </Message>
    ) : '';
    const stateErrorDetails = stateError ? (
      <Message role="alert" negative>
        <Message.Header>
          <FormattedMessage
            {...messages.stateError}
            values={{ supportLink: <Link href={`mailto:${process.env.SUPPORT_EMAIL}`}>PowerSchool Support</Link> }}
          />
        </Message.Header>
      </Message>
    ) : '';
    return (
      <Container text>
        <PageTitle title="Forgot Password" category="Intersect" />
        <Step.Group fluid as="ul" className="no-padding">
          <Step as="li" active={step === 1} completed={step > 1}>
            <Step.Title>
              <WCAGTab
                id="set-email-tab"
                ref={this.emailRef}
                tabIndex={step === 1 ? '0' : '-1'}
                ariaSelected={step === 1 ? 'true' : 'false'}
              >
                <FormattedMessage {...messages.emailStep} />
              </WCAGTab>
            </Step.Title>
          </Step>
          <Step as="li" active={step === 2} completed={step > 2} disabled={step < 2}>
            <Step.Title>
              <WCAGTab
                id="set-password-tab"
                ref={this.passwordRef}
                tabIndex={step === 2 ? '0' : '-1'}
                ariaSelected={step === 2 ? 'true' : 'false'}
              >
                <FormattedMessage {...messages.passwordStep} />
              </WCAGTab>
            </Step.Title>
          </Step>
        </Step.Group>
        <Header as="h1"><FormattedMessage id="forgot-password.header" defaultMessage="Forgot Password" /></Header>
        <Dimmer.Dimmable as={Segment}>
          <Dimmer role="progressbar" aria-describedby="Waiting..." aria-busy={requesting} inverted active={requesting}>
            <Loader active={requesting} />
          </Dimmer>
          { step === 1 ? <EnterEmail onSubmit={this.forgotPassword} /> : ''}
          { step === 2 ? <VerifySetPassword username={username} onSubmit={this.setPassword} /> : ''}
          { step === 3 ? <Success onSignIn={this.signIn} /> : ''}
          { errorDetails }
          { stateErrorDetails }
        </Dimmer.Dimmable>
      </Container>
    );
  }
}

export const ForgotPasswordComponent = ForgotPassword;
export default connect(null, { login })(ForgotPasswordComponent);
